import { Button, ColumnLayout, Container, ContentLayout, Form, FormField, Header, Input, SpaceBetween, Textarea } from '@cloudscape-design/components';
import { useCallback, useMemo, useState } from 'react';
import { SurveyData } from '../types';
import { TagIcon } from '../../utilities/tagIcon';
import { isValidTag } from '../../../utils/validations'; // Adapt this function for feature validation
import { handleFieldChange } from '../../../utils/fieldChangeUtils';
import { useBlockerLogic } from '../../../utils/hooks/useBlocker';
import useChangeDetector from '../../../utils/useChangeDetector';
import { ConfirmCancelModal } from '../../../components/confirmCancelModal';
import { v4 as uuidv4 } from 'uuid';

interface Question {
  uuid: string;
  question: string;
}

type Questions = Array<Question>;
interface EditSurveyFormProps {
  survey?: SurveyData;
  onSubmitForm: (surveyUpdate: SurveyData) => Promise<void> | void;
}

export default function EditSurveyForm({ onSubmitForm, survey: initialSurvey }: Readonly<EditSurveyFormProps>) {
  const { feature, description, introPrompt, outroPrompt, min, max, questions, flags, tag } = initialSurvey || {
    feature: '',
    description: '',
    introPrompt: '',
    outroPrompt: '',
    questions: [],
    flags: [],
    tag: '',
    min: 0,
    max: 9,
  };

  const initialSurveyQuestions = useMemo(() => 
    questions.reduce((response: Questions, question) => [...response, { uuid: uuidv4(), question }], []), [questions]);


  const {
    item: survey,
    setItem: setSurvey,
    changesDetected,
  } = useChangeDetector<Omit<SurveyData, 'questions'>>({ feature, description, introPrompt, outroPrompt, min, max, flags, tag });

  const {
    item: surveyQuestions,
    setItem: setSurveyQuestions,
    changesDetected: questionsChanged,
  } = useChangeDetector<Questions>(initialSurveyQuestions);

  const [tagError, setTagError] = useState<boolean>(false);
  const [tagErrorMessage, setTagErrorMessage] = useState<string>('');
  const [featureError, setFeatureError] = useState<boolean>(false);
  const [featureErrorMessage, setFeatureErrorMessage] = useState<string>('');
  const [submitted, setSubmitted] = useState(false);

  const setField = useCallback(handleFieldChange(setSurvey), [setSurvey]);

  const { blocker, handleCancel, handleCloseCancelModal, handleConfirmCancel } = useBlockerLogic({
    changesDetected: changesDetected || questionsChanged,
    path: 'surveys',
    formSubmitted: submitted,
  });

  const updateQuestion = useCallback((index: number, text: string) => {

    setSurveyQuestions((prevQuestions) => {
      const updatedQuestions = [...prevQuestions];
      updatedQuestions[index].question = text;
      return updatedQuestions;
    });
  }, [setSurveyQuestions]);

  const addQuestion = useCallback(() => {
    setSurveyQuestions((prevQuestions) => [...prevQuestions, { uuid: uuidv4(), question: '' }]);
  }, [setSurveyQuestions]);

  const removeQuestion = useCallback((index: number) => {
    setSurveyQuestions((prevQuestions) => {
      const updatedQuestions = [...prevQuestions];
      updatedQuestions.splice(index, 1);
      return updatedQuestions;
    });
  }, [setSurveyQuestions]);

  const validateTag = useCallback((value: string) => {
    // Define patterns
    const validTag = isValidTag(value);

    if (!validTag) {
      setTagError(true);
      setTagErrorMessage('Only lowercase alphanumeric, - _ and + supported');
    } else {
      setTagError(false);
      setTagErrorMessage('');
    }
  }, []);

  const validateFeature = useCallback((value: string) => {
    const featurePattern = /^[a-zA-Z0-9-_+]+$/;

    if (!featurePattern.test(value)) {
      setFeatureError(true);
      setFeatureErrorMessage('Only alphanumeric, - _ and + supported, no spaces allowed');
    } else {
      setFeatureError(false);
      setFeatureErrorMessage('');
    }
  }, []);

  const handleTagChange = useCallback((tag: string) => {
    setField('tag', tag);
    validateTag(tag);
  }, [setField, validateTag]);

  const handleFeatureChange = useCallback((feature: string) => {
    setField('feature', feature);
    validateFeature(feature);
  }, [setField, validateFeature]);

  const saveSurvey = useCallback(async () => {
    setSubmitted(true);
    const completeSurvey: SurveyData = {
      ...survey,
      questions: surveyQuestions.map(({ question }) => question),
    };

    onSubmitForm(completeSurvey);
  }, [survey, surveyQuestions, onSubmitForm]);

  const validateRange = useMemo(() => {
    const minimum = Number(survey.min);
    const maximum = Number(survey.max);

    return maximum > minimum;
  }, [survey.min, survey.max]);

  const validateForm = useMemo(() => {
    if (survey.feature === '' || featureError) {
      return false;
    }

    if (surveyQuestions.length === 0 || !!surveyQuestions.filter(({ question }) => question.length === 0).length) {
      return false;
    }

    if (!validateRange) {
      return false;
    }

    return true;
  }, [survey.feature, featureError, surveyQuestions, validateRange]);

  const isEditMode = !!initialSurvey?.feature;

  const createEditLabel = isEditMode ? `Edit Survey Group: ${survey.feature}` : 'Create a new survey';
  const canSubmit = (changesDetected || questionsChanged) && !tagError && !featureError && validateForm;

  return (
    <>
      <ContentLayout header={<Header variant='h1'></Header>}>
        <Container
          header={
            <Header
              variant='h2'
              description={isEditMode ? 'Use this form to edit the survey' : 'Use this form to create a survey'}
            >
              {createEditLabel}
            </Header>
          }
        >
          <form onSubmit={(event) => event.preventDefault()}>
            <Form
              actions={
                <SpaceBetween
                  direction='horizontal'
                  size='xs'
                >
                  <Button
                    formAction='none'
                    variant='link'
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant='primary'
                    onClick={saveSurvey}
                    disabled={!canSubmit}
                    loading={submitted}
                  >
                    Submit
                  </Button>
                </SpaceBetween>
              }
              errorText=''
            >
              <SpaceBetween size='m'>
                <Container header={<Header variant='h2'>Name and Description</Header>}>
                  <SpaceBetween
                    direction='vertical'
                    size='xl'
                  >
                    <ColumnLayout
                      columns={2}
                      variant='text-grid'
                    >
                      <FormField
                        label='Provide a Survey name'
                        constraintText='Only alphanumeric, - _ and + supported'
                        errorText={featureErrorMessage}
                      >
                        <Input
                          onChange={({ detail }) => handleFeatureChange(detail.value)}
                          value={survey.feature}
                          disabled={isEditMode}
                        />
                      </FormField>
                      <FormField
                        constraintText='Only alphanumeric, - _ and + supported'
                        errorText={tagErrorMessage}
                        label={
                          <>
                            Tag your Survey group <TagIcon />
                          </>
                        }
                      >
                        <Input
                          onChange={({ detail }) => handleTagChange(detail.value)}
                          value={survey.tag}
                        />
                      </FormField>
                    </ColumnLayout>
                    <FormField
                      label='Description'
                      stretch
                    >
                      <Textarea
                        onChange={({ detail }) => setField('description', detail.value)}
                        value={survey.description}
                      />
                    </FormField>
                  </SpaceBetween>
                </Container>
                <Container
                  header={
                    <Header
                      variant='h2'
                      description='Messaging associated to the survey'
                    >
                      Messaging
                    </Header>
                  }
                >
                  <SpaceBetween size='s'>
                    <FormField
                      stretch={true}
                      label='Introduction message (optional)'
                      description='The message to play at the start of the survey'
                    >
                      <Input
                        disabled={submitted}
                        type='text'
                        value={survey.introPrompt}
                        onChange={(event) => setField('introPrompt', event.detail.value)}
                      ></Input>
                    </FormField>
                    <FormField
                      stretch={true}
                      label='Conclusion message (optional)'
                      description='The message to play at the end of the survey'
                    >
                      <Input
                        disabled={submitted}
                        type='text'
                        value={survey.outroPrompt}
                        onChange={(event) => setField('outroPrompt', event.detail.value)}
                      ></Input>
                    </FormField>
                  </SpaceBetween>
                </Container>
                <Container
                  header={
                    <Header
                      variant='h2'
                      description='The range for valid input'
                    >
                      Input range
                    </Header>
                  }
                >
                  <SpaceBetween
                    direction='horizontal'
                    size='s'
                  >
                    <FormField
                      stretch={false}
                      label='From'
                    >
                      <Input
                        disabled={submitted}
                        type='number'
                        value={survey.min.toString()}
                        invalid={!validateRange}
                        onChange={(event) => setField('min', parseInt(event.detail.value))}
                      ></Input>
                    </FormField>
                    <FormField
                      stretch={false}
                      label='To'
                    >
                      <Input
                        disabled={submitted}
                        type='number'
                        value={survey.max.toString()}
                        invalid={!validateRange}
                        onChange={(event) => setField('max', parseInt(event.detail.value))}
                      ></Input>
                    </FormField>
                  </SpaceBetween>
                </Container>
                <Container
                  header={
                    <Header
                      variant='h2'
                      description='The content of the survey'
                      actions={
                        <SpaceBetween
                          direction='horizontal'
                          size='xs'
                        >
                          <Button
                            disabled={submitted}
                            onClick={addQuestion}
                          >
                            Add question
                          </Button>
                        </SpaceBetween>
                      }
                    >
                      Questions
                    </Header>
                  }
                >
                  <SpaceBetween size='l'>
                    {surveyQuestions.map((item, index) => (
                      <SpaceBetween
                        size='xs'
                        key={item.uuid}
                      >
                        <FormField
                          label={`Question ${index + 1}`}
                          // stretch={true}
                          secondaryControl={
                            <Button
                              variant='inline-icon'
                              onClick={() => removeQuestion(index)}
                              iconName='remove'
                            />
                          }
                        >
                          <Input
                            disabled={submitted}
                            type='text'
                            value={item.question}
                            onChange={(event) => updateQuestion(index, event.detail.value)}
                          ></Input>
                        </FormField>
                      </SpaceBetween>
                    ))}
                  </SpaceBetween>
                </Container>
              </SpaceBetween>
            </Form>
          </form>
        </Container>
      </ContentLayout>
      {blocker.state === 'blocked' && (
        <ConfirmCancelModal
          {...{
            onCancel: handleCloseCancelModal,
            onConfirm: handleConfirmCancel,
            cancelMessage: 'Are you sure you want to close? Any unsaved changes will be lost.',
          }}
        />
      )}
    </>
  );
}
